import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Editor UX del bloque `summary_table` de la plantilla `mejores`
// (TC-133-1.3.1.2). Conecta con data-controller="summary-table".
//
// Permite al redactor:
//   1. Buscar un broker por nombre/slug (autocomplete `vendebrokers`, vía un
//      input Awesomplete con `data-autocomplete-url`) y añadirlo como fila.
//   2. Reordenar las filas por drag&drop (SortableJS) — el orden es dato.
//   3. Quitar una fila (no borra el broker de la colección global; solo lo
//      saca de esta tabla).
//   4. Ver un preview por fila (logo, nombre, métricas configuradas, CTA),
//      servido por `TrixController#summary_table_row`.
//
// Persistencia: cada fila lleva un input oculto `summary_table[ids_brokers][]`
// con el `broker_id` canónico. El orden de los inputs en el DOM = orden de la
// tabla, que es lo que Rails recibe al hacer submit (params en orden de
// aparición). Las columnas (`summary_table[columnas][]`) las pinta el server en
// el partial del editor; este controller solo gestiona las filas de brokers.
//
// El partial del editor se renderiza SIEMPRE dentro del contenedor
// `.mejores-blocks` de `posts/edit`, que arranca oculto salvo que el artículo sea
// `mejores`; el controller `article-type-notice` lo muestra/oculta en cliente
// según el tipo vivo del selector, así que el bloque no es visible fuera de esa
// plantilla (US-133-1.3.1, primer escenario Gherkin).
export default class extends Controller {
  static targets = ['input', 'brokerIdInput', 'list', 'row', 'empty']
  static values = {
    previewUrl: String,
    columns: Array
  }

  connect () {
    this.sortable = Sortable.create(this.listTarget, {
      animation: 150,
      draggable: '.summary-table__row',
      handle: '.summary-table__drag-handle',
      // `forceFallback` evita el drag&drop nativo de HTML5 (cuya "drag image" es
      // una captura del navegador, inconsistente y con reflow del contenido
      // flex de la fila). En su lugar SortableJS clona la fila y la mueve con el
      // cursor, lo que da un arrastre suave y predecible cross-browser.
      forceFallback: true,
      fallbackOnBody: true,
      fallbackTolerance: 3,
      ghostClass: 'summary-table__row--ghost', // hueco donde caerá la fila
      chosenClass: 'summary-table__row--chosen', // fila origen mientras se arrastra
      dragClass: 'summary-table__row--active', // fila marcada como en arrastre
      fallbackClass: 'summary-table__row--floating', // clon que sigue al cursor
      // Al soltar una fila, el orden de la tabla cambió: notifica a quien dependa
      // de ella (el destacado del mes lee este orden vivo).
      onEnd: () => this.dispatchChanged()
    })
    this.refreshEmptyState()
    // Publica el estado inicial de la tabla para que otros bloques (destacado del
    // mes) puedan sincronizarse sin recargar.
    this.dispatchChanged()
  }

  disconnect () {
    this.sortable?.destroy()
    this.sortable = null
  }

  // Añade el broker seleccionado en el autocomplete como una fila nueva.
  // El autocomplete (`vendebrokers`) deja un permalink en el input; el preview
  // endpoint lo resuelve a `broker_id` (atributo canónico). Evita duplicados:
  // un broker ya presente en la tabla no se añade dos veces.
  async add (event) {
    event?.preventDefault()
    const permalink = this.inputTarget.value.trim()
    if (permalink === '') return

    const params = new URLSearchParams()
    params.set('broker_permalink', permalink)
    this.columnsValue.forEach(column => params.append('columnas[]', column))

    const response = await fetch(`${this.previewUrlValue}?${params.toString()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) {
      window.alert('No se ha podido cargar el broker. Inténtalo de nuevo.')
      return
    }

    const data = await response.json()
    if (!data.brokerId) {
      window.alert('No se ha encontrado un broker para esa búsqueda.')
      return
    }
    if (this.brokerIds.includes(String(data.brokerId))) {
      window.alert('Ese broker ya está en la tabla.')
      this.inputTarget.value = ''
      return
    }

    this.listTarget.insertAdjacentHTML('beforeend', this.rowMarkup(data))
    this.inputTarget.value = ''
    this.refreshEmptyState()
    this.dispatchChanged()
  }

  // Quita la fila del broker. NO borra el broker de la colección global: solo
  // saca su `broker_id` de este bloque (US-133-1.3.1, escenario "Quitar un
  // broker"). Al hacer submit, ese broker deja de estar en `ids_brokers`.
  remove (event) {
    event.preventDefault()
    event.target.closest('.summary-table__row')?.remove()
    this.refreshEmptyState()
    this.dispatchChanged()
  }

  // `broker_id`s actualmente en la tabla, como strings, en orden de DOM.
  get brokerIds () {
    return this.brokerIdInputTargets.map(input => input.value)
  }

  // Brokers actualmente en la tabla, en orden de DOM, como `{ id, name }`. El
  // `id` sale del input oculto canónico (`brokerIdInput`); el `name`, del preview
  // ya renderizado en la fila (`.summary-table-row__name`). Es el contrato que el
  // destacado del mes (`featured-broker-editor`) consume para repoblar su selector.
  get brokersDetail () {
    return this.rowTargets.map(row => ({
      id: row.querySelector('[data-summary-table-target="brokerIdInput"]')?.value,
      name: row.querySelector('.summary-table-row__name')?.textContent.trim() ?? ''
    })).filter(broker => broker.id)
  }

  // Notifica que la composición de la tabla (altas, bajas o reordenaciones)
  // cambió. El evento burbujea hasta `document`, donde el controller del destacado
  // del mes escucha para habilitar/repoblar su selector en vivo, sin recargar
  // (ronda 13 de PRD-133-1, Opción A).
  dispatchChanged () {
    this.dispatch('changed', { detail: { brokers: this.brokersDetail } })
  }

  refreshEmptyState () {
    if (!this.hasEmptyTarget) return
    this.emptyTarget.hidden = this.rowTargets.length > 0
  }

  rowMarkup (data) {
    const template = document.createElement('template')
    template.innerHTML = `
      <li class="summary-table__row" data-summary-table-target="row">
        <span class="summary-table__drag-handle" aria-label="Reordenar" title="Arrastra para reordenar">⠿</span>
        <input type="hidden" name="post[summary_table][ids_brokers][]"
               value="${data.brokerId}" data-summary-table-target="brokerIdInput">
        <div class="summary-table__row-preview">${data.content}</div>
        <button type="button" class="summary-table__remove"
                aria-label="Quitar broker de la tabla"
                data-action="summary-table#remove"><span aria-hidden="true">×</span></button>
      </li>
    `.trim()
    return template.innerHTML
  }
}
